import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  useWebsiteBlogs,
  useWebsiteBlogMeta,
} from '@/services/httpServices/queries/useWebsite'
import { flattenBlogTree } from '@/data/blog'

/**
 * The blog widget sidebar (search, categories list, recent posts, popular tags),
 * matching the hospital template markup in `fastaid-html/blog.html` and
 * `fastaid-html/blog-details.html`: a flat list of category card rows
 * (`ul.category-list > li > a`) and a wrapping row of tag pills
 * (`div.widget-tag-box > a.widget-tag-item`). Category and tag links point at
 * the `/blog/category/:slug` and `/blog/tag/:slug` archive routes.
 */
export function BlogSidebar() {
  const navigate = useNavigate()
  const [term, setTerm] = useState('')

  const { posts } = useWebsiteBlogs()
  const { categories, tagTree } = useWebsiteBlogMeta()
  const recent = posts.slice(0, 3)
  const categoryList = flattenBlogTree(categories)
  const tagList = flattenBlogTree(tagTree)

  function handleSearch(event: FormEvent) {
    event.preventDefault()
    const query = term.trim()
    navigate(query ? `/blog?search=${encodeURIComponent(query)}` : '/blog')
  }

  return (
    <div className="widget-sidebar">
      {/* widget search */}
      <div className="widget widget-search">
        <div className="widget-title">
          <h3>Search</h3>
        </div>
        <div className="widget-content">
          <form action="#" onSubmit={handleSearch}>
            <div className="form-group mb-0">
              <div className="form-floating field-inner">
                <input
                  id="widgetsearch"
                  name="widgetsearch"
                  className="form-control white-field"
                  placeholder="Search here...."
                  type="text"
                  autoComplete="off"
                  value={term}
                  onChange={(event) => setTerm(event.target.value)}
                />
                <label htmlFor="widgetsearch">Search </label>
                <button type="submit" aria-label="search submit">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* widget categories list */}
      <div className="widget widget-categories-list">
        <div className="widget-title">
          <h3>Categories List</h3>
        </div>
        <div className="widget-content">
          <ul className="category-list">
            {categoryList.map((category) => (
              <li key={category.id}>
                <Link to={`/blog/category/${encodeURIComponent(category.slug)}`}>
                  <span className="categories-name">{category.name}</span>{' '}
                  <span className="categories-count">({category.count})</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* widget recent post */}
      <div className="widget widget-recent-post">
        <div className="widget-title">
          <h3>Recent Post</h3>
        </div>
        <div className="widget-content">
          {recent.map((post, index) => (
            <div className="recent-post-item d-flex" key={post.slug}>
              <div className="recent-post-image flex-shrink-0">
                <figure className="image-anime">
                  <img src={`/assets/images/widgets/recent-post/recent-post-${index + 1}.jpg`} alt={post.title} />
                </figure>
              </div>
              <div className="recent-post-content flex-grow-1 ms-3">
                <h4>
                  <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                </h4>
                <p className="post-date">
                  <i className="fa-solid fa-calendar-days"></i>
                  {post.date}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* widget tag */}
      <div className="widget widget-tag">
        <div className="widget-title">
          <h3>Popular Tags</h3>
        </div>
        <div className="widget-content">
          <div className="widget-tag-box d-flex flex-wrap gap-3">
            {tagList.map((tag) => (
              <Link
                key={tag.id}
                to={`/blog/tag/${encodeURIComponent(tag.slug)}`}
                className="widget-tag-item"
              >
                {tag.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
